import { useState } from "react";
import {
  type BenchmarkCohortMode,
  useBenchmarkConfig,
} from "./use-benchmark-config";

/**
 * Hook that manages all UI interactions for benchmark configuration
 * Extracts this logic from page components for better separation of concerns
 */
export function useBenchmarkUI() {
  // Modal visibility state
  const [showBenchmarkModal, setShowBenchmarkModal] = useState(false);

  // Access to benchmark configuration
  const {
    benchmarkConfig,
    isBenchmarkEnabled,
    saveBenchmarkConfig,
    clearBenchmarkConfig,
    setBenchmarkEnabled,
    hasBenchmarkConfig,
  } = useBenchmarkConfig();

  /**
   * Handles saving benchmark configuration
   */
  const handleSaveBenchmarkConfig = async (
    filters: Record<string, unknown>,
    campaignCount: number,
    excludeCurrentCampaign: boolean = true,
    mode: BenchmarkCohortMode = "static",
    selectedCampaignIds: string[] = [],
  ) => {
    if (
      Object.keys(filters).length === 0 &&
      campaignCount === 0 &&
      selectedCampaignIds.length === 0
    ) {
      await clearBenchmarkConfig();
    } else {
      await saveBenchmarkConfig(
        filters,
        campaignCount,
        excludeCurrentCampaign,
        mode,
        selectedCampaignIds,
      );
    }
  };

  /**
   * Handles benchmark toggle changes
   */
  const handleBenchmarkToggleChange = async (enabled: boolean) => {
    // Update the enabled state immediately for UI responsiveness
    setBenchmarkEnabled(enabled);

    // Show config modal when enabling without existing configuration
    if (enabled && !hasBenchmarkConfig) {
      setShowBenchmarkModal(true);
    }
  };

  return {
    // State
    benchmarkConfig,
    isBenchmarkEnabled,
    showBenchmarkModal,
    hasBenchmarkConfig,

    // Actions
    setShowBenchmarkModal,
    handleSaveBenchmarkConfig,
    handleBenchmarkToggleChange,

    // Helper function to open the config modal
    openBenchmarkModal: () => setShowBenchmarkModal(true),
  };
}
